<template>
  <!-- Own header instead of PageChrome's: its header is a sibling of the content column, so a
       container query there could not reach the Export/Import buttons. -->
  <PageChrome :show-header="false">
    <div class="integrationsBody">
      <header v-if="!shellOwnsTitle || transferEnabled" class="pageHead">
        <div v-if="!shellOwnsTitle" class="title">Integrations</div>
        <div class="pageActions">
          <button v-if="transferEnabled" class="btn" @click="exportIntegrations">Export</button>
          <button v-if="transferEnabled" class="btn" @click="triggerImport">Import</button>
          <input
            v-if="transferEnabled"
            ref="importInput"
            type="file"
            accept="application/json"
            style="display:none"
            @change="onImportFile"
          />
        </div>
      </header>

      <div v-if="error" class="error">{{ error }}</div>

      <div v-if="connectedBannerName" class="modal-banner modal-banner-success" style="margin-bottom:16px;">
        <span>&#x2713; {{ connectedBannerName }} connected successfully</span>
      </div>

      <!-- Wrapper so the container query can hide this without unmounting it: the banner fetches
           in onMounted, and a v-if would refetch on every expand. -->
      <div class="sorWrap">
        <SystemOfRecordBanner ref="sorBanner" @changed="onSorChanged" />
      </div>

      <div class="section-heading">Workspace integrations</div>
      <div class="grid">
        <!-- A real button, so role/focus/Enter/Space come from the platform: compact mode hides the
             Manage/Connect label, leaving the card itself as the only control. -->
        <button
          v-for="it in workspaceIntegrations"
          :key="it.id"
          type="button"
          class="card"
          :class="{ isConnected: crmOfRecord === it.id || isConnected(it.id) }"
          :title="it.name"
          :aria-label="`${it.name} — ${crmOfRecord === it.id ? 'system of record' : isConnected(it.id) ? 'connected' : 'not connected'}`"
          @click="open(it)"
        >
          <div class="logo" :class="`logo-${it.id}`">
            <img
              v-if="it.domain && !integrationIconErrored[it.id]"
              :src="integrationIconUrl(it.domain)"
              :alt="it.name"
              class="integration-icon"
              loading="lazy"
              draggable="false"
              @error="integrationIconErrored[it.id] = true"
            />
            <span v-else class="logo-text">{{ it.short }}</span>
          </div>

          <div class="name">{{ it.name }}</div>
          <div class="desc">{{ it.description }}</div>

          <div class="bottom">
            <span v-if="crmOfRecord === it.id" class="badge success">System of record</span>
            <span v-else-if="isConnected(it.id)" class="badge success">Connected</span>
            <span v-else class="badge">Not connected</span>

            <div style="display:flex; gap:8px;">
              <!-- Label, not a control: the whole card is the button, so the icon stays clickable
                   once compact mode hides everything but the logo. -->
              <span class="btn" aria-hidden="true">{{ isConnected(it.id) ? 'Manage' : 'Connect' }}</span>
            </div>
          </div>
        </button>
      </div>

      <div class="section-heading" style="margin-top:28px;">Personal integrations</div>
      <div class="grid">
        <button
          v-for="it in personalIntegrations"
          :key="it.id"
          type="button"
          class="card"
          :class="{ isConnected: isPersonalConnected(it.id) }"
          :title="it.name"
          :aria-label="`${it.name} — ${isPersonalConnected(it.id) ? 'connected' : 'not connected'}`"
          @click="open(it)"
        >
          <div class="logo" :class="`logo-${it.id}`">
            <img
              v-if="it.domain && !integrationIconErrored[it.id]"
              :src="integrationIconUrl(it.domain)"
              :alt="it.name"
              class="integration-icon"
              loading="lazy"
              draggable="false"
              @error="integrationIconErrored[it.id] = true"
            />
            <span v-else class="logo-text">{{ it.short }}</span>
          </div>

          <div class="name">{{ it.name }}</div>
          <div class="desc">{{ it.description }}</div>

          <div class="bottom">
            <span v-if="isPersonalConnected(it.id)" class="badge success">Connected</span>
            <span v-else class="badge">Not connected</span>

            <div style="display:flex; gap:8px;">
              <span class="btn" aria-hidden="true">{{ isPersonalConnected(it.id) ? 'Manage' : 'Connect' }}</span>
            </div>
          </div>
        </button>

      </div>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import SystemOfRecordBanner from '../components/integrations/SystemOfRecordBanner.vue'
import { useChatScope } from '../composables/useChatScope'
import { integrationRegistry } from '../../integrations/registry'
import { apiFetch } from '../../services/api'
import { googleFaviconUrl } from '../../services/favicons'
import { useIntegrationStatus } from '../../hooks/useIntegrationStatus'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { INTEGRATION_CONTEXT_TYPE } from '../composables/integrationContext'
import { useIntegrationsStatus } from '../composables/useIntegrationsStatus'
import { useCurrentUser } from '../../services/user'
import { useShellOwnsTitle } from '../composables/shellTitle'

const { isDialerOnly } = useCurrentUser()
const shellOwnsTitle = useShellOwnsTitle()
const { reloadIntegrations } = useIntegrationStatus()
const { setContext, openDrawer } = useContextDrawer()
// Workspace status + CRM-of-record are shared with the docked panel, so a connect/disconnect there
// reactively updates this grid.
const { configuredByProvider, crmOfRecord, transferEnabled, justConnectedProvider, refresh } = useIntegrationsStatus()

const { setScope, clearScope } = useChatScope()

const error = ref<string>('')
const connectedBannerName = ref<string | null>(null)

// Dialer-only tenants see only the CRM cards — the export tab's call/email task
// mappings are what their dialed-call exports ride; everything else is full-product.
const DIALER_ONLY_CRM_IDS = new Set(['salesforce', 'hubspot', 'pipedrive'])
const workspaceIntegrations = integrationRegistry.filter(
  p => p.section === 'workspace' && (!isDialerOnly.value || DIALER_ONLY_CRM_IDS.has(p.id))
)
const personalIntegrations = integrationRegistry.filter(
  p => p.section === 'personal' && (!isDialerOnly.value || DIALER_ONLY_CRM_IDS.has(p.id))
)

const personalConfigured = ref<Record<string, boolean>>({})

async function loadPersonalStatus() {
  // Non-CRM status fetches would 403 for dialer-only tenants — apiFetch
  // hard-redirects on 403 even inside allSettled, so never issue them.
  const probes = [
    apiFetch('/user/integrations/pipedrive').then(resp => {
      personalConfigured.value['pipedrive'] = Boolean(resp?.connected)
    }),
  ]
  if (!isDialerOnly.value) {
    probes.push(
      apiFetch('/user/integrations/google-workspace').then(resp => {
        personalConfigured.value['google-workspace'] = Boolean(resp?.connected)
      }),
      apiFetch('/user/integrations/outreach').then(resp => {
        personalConfigured.value['outreach'] = Boolean(resp?.connected)
      }),
    )
  }
  await Promise.allSettled(probes)
}

function isPersonalConnected(id: string | number) {
  return Boolean(personalConfigured.value?.[id])
}

// The docked panel sets justConnectedProvider when an integration connects → flash a success banner
// and refresh personal status for the grid (workspace status already refreshed by the panel).
watch(justConnectedProvider, (provider) => {
  if (!provider) return
  const plugin = integrationRegistry.find(p => p.id === provider)
  connectedBannerName.value = plugin?.name || 'Integration'
  setTimeout(() => { connectedBannerName.value = null }, 6000)
  void loadPersonalStatus()
  void reloadIntegrations()
  justConnectedProvider.value = null
})

const integrationIconErrored = reactive<Record<string, boolean>>({})

function integrationIconUrl(domain: string) {
  return googleFaviconUrl(domain, { size: 64 })
}

const importInput = ref<HTMLInputElement | null>(null)

function isConnected(id: string | number) {
  return Boolean(configuredByProvider.value?.[id])
}

// Open the docked IntegrationsContextPanel for any integration (CRM or not).
function open(it: { id: string }) {
  error.value = ''
  setContext({ type: INTEGRATION_CONTEXT_TYPE, provider: it.id })
  openDrawer()
}

function onSorChanged(crm: string) {
  crmOfRecord.value = crm || ''
}

async function exportIntegrations() {
  error.value = ''
  try {
    const data = await apiFetch('/settings/integrations/export')
    const pretty = JSON.stringify(data, null, 2)
    const blob = new Blob([pretty], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = url
      a.download = 'integrations-export.json'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      URL.revokeObjectURL(url)
    }
  } catch (e) {
    error.value = e?.message || 'Export failed'
  }
}

function triggerImport() {
  error.value = ''
  if (importInput.value) {
    importInput.value.value = ''
    importInput.value.click()
  }
}

async function onImportFile(e: Event & { target: { files: File[] } }) {
  error.value = ''
  const file = e?.target?.files?.[0]
  if (!file) return
  try {
    const text = await file.text()
    const obj = text ? JSON.parse(text) : null
    await apiFetch('/settings/integrations/import', {
      method: 'POST',
      body: obj || {},
    })
    await Promise.all([refresh(), loadPersonalStatus()])
    void reloadIntegrations()
  } catch (err) {
    error.value = err?.message || 'Import failed'
  }
}

onMounted(async () => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })

  // Auto-open the docked panel when returning from an external OAuth flow.
  const RETURN_PROVIDER: Record<string, string> = {
    google_workspace: 'google-workspace', pipedrive: 'pipedrive', hubspot: 'hubspot', salesforce: 'salesforce',
  }
  const params = new URLSearchParams(window.location.search)
  const returning = RETURN_PROVIDER[params.get('connection') || ''] || params.get('open')
  if (returning && integrationRegistry.some(p => p.id === returning)) {
    setContext({ type: INTEGRATION_CONTEXT_TYPE, provider: returning })
    openDrawer()
  }

  try {
    await refresh()
  } catch (e) {
    error.value = e?.message || 'Failed'
  }

  await loadPersonalStatus()
})

onBeforeUnmount(() => { clearScope() })
</script>

<style scoped>
.error { color: var(--danger); margin-bottom: 10px; }

.section-heading {
  font-size: 13px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

/* Query the content column, not the viewport: the drawer takes its room from this column, so
   viewport breakpoints never fired and the cards just compressed (~120px each with both panels
   open). Thresholds are re-derived against this element's own width — the old viewport numbers
   also covered 112px of gutters plus both panels — targeting ~200px per card at a 14px gap. */
.integrationsBody {
  container-type: inline-size;
}

.pageHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}
.pageHead .title { display: inline-flex; align-items: center; min-width: 0; }
.pageActions { display: inline-flex; align-items: center; gap: var(--space-3); margin-left: auto; }

.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

/* Shed a column at a time. A card stops being readable around 200px — the badge + action row is
   what needs the width — so each step is set where the next count still clears that. */
@container (max-width: 840px) {
  .grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@container (max-width: 640px) {
  .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pageActions { display: none; }
}

@container (max-width: 420px) {
  .grid { grid-template-columns: minmax(0, 1fr); }
}

.card {
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--surface);
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-height: 168px;
  cursor: pointer;
  text-align: left;
}
.card:hover { border-color: var(--color-border-strong, var(--border)); }
.card:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* Card internals — unchanged at full size. */
.logo {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--badge-bg);
  display: grid;
  place-items: center;
}

.integration-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  border-radius: 6px;
}

.logo-text {
  font-weight: 800;
  font-size: 13px;
  color: var(--text);
}

.name {
  margin-top: 12px;
  font-weight: 700;
}

.desc {
  margin-top: 6px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
  flex: 1;
}

.bottom {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.hint {
  color: var(--muted);
  font-size: 13px;
  margin-top: 6px;
  line-height: 1.35;
}

/* ── COMPACT: icons only, the dialer rail's avatar collapse — driven by the width this column
   actually has, not by drawer state. Last resort, well below the single-column step: a lone card
   still reads at ~280px, so this waits until even that is gone. Minimising the drawer widens the
   column and brings the cards back. */
@container (max-width: 260px) {
  .grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .card {
    min-height: 0;
    padding: 0;
    border: 0;
    background: transparent;
    border-radius: var(--radius-full, 999px);
  }
  .name,
  .desc,
  .bottom { display: none; }
  .logo {
    width: 46px;
    height: 46px;
    border-radius: var(--radius-full, 999px);
    position: relative;
  }
  .card:hover .logo { box-shadow: 0 0 0 2px var(--color-border-strong, var(--border)); }
  .card:focus-visible { outline: none; }
  .card:focus-visible .logo { box-shadow: 0 0 0 2px var(--color-accent); }
  /* The one bit of status worth keeping without text. */
  .card.isConnected .logo::after {
    content: '';
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 12px;
    height: 12px;
    border-radius: var(--radius-full, 999px);
    background: var(--color-accent);
    box-shadow: 0 0 0 2px var(--surface);
  }
}

</style>
